import type { InjectionKey, Ref } from 'vue'

import { usePage } from '@inertiajs/vue3'
import { inject, provide, ref, watch } from 'vue'

const FULLSCREEN_KEY: InjectionKey<Ref<boolean>> = Symbol('layout-fullscreen')

/**
 * Hides the sidebar so a wide page can use the whole window.
 *
 * Provided by the layout rather than the page container, because the sidebar is the page
 * container's sibling and an injection there would never reach it.
 *
 * It resets on navigation for the same reason `usePageWidth` does: a page that asked for the
 * whole window must not leave the next one without its navigation.
 */
const provideFullscreen = (): Ref<boolean> => {
  const isFullscreen = ref(false)
  provide(FULLSCREEN_KEY, isFullscreen)

  const page = usePage()
  watch(
    () => page.component,
    () => {
      isFullscreen.value = false
    }
  )

  return isFullscreen
}

const useFullscreen = () => {
  const isFullscreen = inject(FULLSCREEN_KEY, null)

  const toggle = (): void => {
    if (isFullscreen) isFullscreen.value = !isFullscreen.value
  }

  const exit = (): void => {
    if (isFullscreen) isFullscreen.value = false
  }

  return { isFullscreen: isFullscreen ?? ref(false), toggle, exit }
}

export { provideFullscreen, useFullscreen }
